// src/belediye/pages/halkMasasi/HalkMasasiCitizenProfilePage.js
//
// Halk Masası — Vatandaş Profil (Detay sayfası tasarım dili ile UYUMLU)
// - Üst başlık barı: Detay sayfasındaki hm-ad-pagebar ile BİREBİR (BEYAZ, static, sağda aksiyonlar)
// - Sol: Vatandaş bilgileri (detay sayfasındaki Saha kart kombinasyonu ile aynı: saha-acc / saha-kv)
// - Sol panel sticky DEĞİL (sayfayla birlikte kayar)
// - Sağ: Başvuru geçmişi (KAPANMAZ, sabit panel) + gri header geri geldi
// - Arama + Filtreler: Başvuru geçmişi panelinin ÜST toolbar'ında
// - Filtre: yalnızca dropdown (backdrop yok), dışarı tık + ESC kapanır
// - Başvuru kartları: minimal ama göz yormasın (CSS ile soft gri zemin) + açıklama görünür
// - Mobil uyumlu: tek kolon, doğal akış
//
// Route: /yonetim/halk-masasi/vatandas/:citizenId

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  MagnifyingGlassIcon,
  ArrowPathIcon,
  FunnelIcon,
  CalendarDaysIcon,
  UserIcon,
  ChevronRightIcon,
  XMarkIcon,
  HashtagIcon,
  BuildingOffice2Icon,
  HomeModernIcon,
  MapPinIcon,
  DocumentTextIcon,
  Squares2X2Icon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";

import {
  fetchRecentLookups,
  refreshCitizenProfileThunk,
  markLastCitizenLocal,
  lookupCitizenByPhoneThunk,
  selectActiveCitizen,
  selectCitizenApplications,
  selectProfileLoading,
  selectProfileError,
  selectRecentServerLookups,
} from "../../redux/slices/belediyeHalkMasasiSlice";

import { normalizePhone10 } from "../../services/halkMasasiApi";
import TrDateTime from "../../components/utils/TrDateTime";
import { safeText, titleCaseTr, ensureFirstUpperTr } from "../../components/utils/textFormat";

import "./HalkMasasiCitizenProfilePage.css";

const BASE = "/yonetim/halk-masasi";

const DEBUG = String(process.env.REACT_APP_DEBUG_HALK_MASASI || "").trim() === "1";
const log = (...args) => DEBUG && console.log("[HalkMasasiCitizenProfilePage]", ...args);
const logError = (...args) => DEBUG && console.error("[HalkMasasiCitizenProfilePage]", ...args);

// ---------------- Helpers ----------------
// safeText/titleCaseTr/ensureFirstUpperTr artık doğrudan paylaşılan util'den gelir.

const pickCreatedAt = (app) => app?.created_at || app?.createdAt || app?.created || null;
const pickStatusDate = (app) => app?.status_date || app?.statusDate || app?.status_date_at || null;

const pickUnitName = (app) =>
  titleCaseTr(safeText(app?.unit_detail?.name || app?.unit_name || app?.unit?.name || app?.unitName || ""));

const pickNeighborhood = (app) =>
  titleCaseTr(safeText(app?.neighborhood_detail?.name || app?.neighborhood_name || app?.neighborhood?.name || ""));

const pickAddress = (app) => ensureFirstUpperTr(safeText(app?.address || ""));
const pickTracking = (app) => safeText(app?.tracking_number || app?.trackingNumber || "");
const pickAppTitle = (app) => ensureFirstUpperTr(safeText(app?.subject) || `Başvuru #${app?.id ?? "—"}`);

const pickAppDesc = (app) => {
  const d = ensureFirstUpperTr(safeText(app?.description));
  if (!d || d === "—") return "";
  return d.length > 220 ? `${d.slice(0, 220)}…` : d;
};

const statusLabel = (status) => {
  const s = String(status || "").trim();
  if (s === "yeni") return "Yeni";
  if (s === "işleme_alındı") return "İncelemede";
  if (s === "yönlendirildi") return "Yönlendirildi";
  if (s === "çözüldü") return "Çözüldü";
  if (s === "çözülemedi") return "Çözülemedi";
  return s || "—";
};

const statusChip = (status) => {
  const s = String(status || "").trim();
  if (s === "çözüldü") return { cls: "md-chip md-chip-success", icon: CheckCircleIcon, label: "Çözüldü" };
  if (s === "çözülemedi") return { cls: "md-chip md-chip-danger", icon: XCircleIcon, label: "Çözülemedi" };
  if (s === "işleme_alındı") return { cls: "md-chip md-chip-warning", icon: ClockIcon, label: "İncelemede" };
  if (s === "yeni") return { cls: "md-chip md-chip-info", icon: Squares2X2Icon, label: "Yeni" };
  if (s === "yönlendirildi") return { cls: "md-chip md-chip-warning", icon: ArrowPathIcon, label: "Yönlendirildi" };
  return { cls: "md-chip", icon: DocumentTextIcon, label: statusLabel(s) };
};

const safeDateObj = (v) => {
  try {
    const d = new Date(v || 0);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
};

const isInDateRange = (dateObj, fromStr, toStr) => {
  if (!dateObj) return false;
  const from = fromStr ? new Date(`${fromStr}T00:00:00`) : null;
  const to = toStr ? new Date(`${toStr}T23:59:59`) : null;
  if (from && dateObj < from) return false;
  if (to && dateObj > to) return false;
  return true;
};

const STATUS_OPTIONS = [
  { key: "yeni", label: "Yeni" },
  { key: "işleme_alındı", label: "İncelemede" },
  { key: "yönlendirildi", label: "Yönlendirildi" },
  { key: "çözüldü", label: "Çözüldü" },
  { key: "çözülemedi", label: "Çözülemedi" },
];

const pickCitizenIdFromRecent = (x) => {
  const sid = x?.citizen_id ?? x?.citizenId ?? x?.citizen?.id ?? x?.citizen?.pk ?? null;
  return sid != null ? String(sid) : null;
};

const pickPhoneFromRecent = (x) => x?.phone_normalized || x?.phone10 || x?.phone || x?.phone_number || null;

// NOT masked (per request)
const formatPhoneDisplay = (raw) => {
  const digits = String(raw ?? "").replace(/\D+/g, "");
  if (!digits) return "—";
  const p10 = normalizePhone10(digits);
  if (p10 && p10.length === 10) return `0${p10}`;
  if (digits.length === 11 && digits.startsWith("0")) return digits;
  return digits;
};

// ---------------- UI primitives (detay sayfası ile aynı sınıf ailesi: saha-*) ----------------
function KeyValueList({ items }) {
  return (
    <dl className="saha-kv">
      {items.map((it) => (
        <div key={it.key} className="saha-kv-row">
          <dt className="saha-kv-key">{it.key}</dt>
          <dd className="saha-kv-val">{it.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function StaticCard({ icon: Icon, title, rightSlot = null, children }) {
  return (
    <section className="md-card saha-acc hm-profile-static">
      <div className="saha-acc-head hm-static-head">
        <div className="saha-acc-head-left">
          {Icon ? <Icon className="saha-acc-icon" /> : null}
          <div className="saha-acc-head-text">
            <div className="saha-acc-title">{title}</div>
          </div>
        </div>
        <div className="saha-acc-head-right">{rightSlot}</div>
      </div>
      <div className="saha-acc-body">{children}</div>
    </section>
  );
}

export default function HalkMasasiCitizenProfilePage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { citizenId } = useParams();

  const cid = useMemo(() => String(citizenId || "").trim(), [citizenId]);

  const activeCitizen = useSelector(selectActiveCitizen);
  const apps = useSelector(selectCitizenApplications);
  const loading = useSelector(selectProfileLoading);
  const error = useSelector(selectProfileError);
  const recentServer = useSelector(selectRecentServerLookups);

  // search + filters state (toolbar on right panel)
  const [q, setQ] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [statusSet, setStatusSet] = useState(() => new Set());

  const [localError, setLocalError] = useState("");

  // prevent repeated init calls
  const initDoneRef = useRef({ cid: null, recentFetched: false, bootstrapTried: false });

  // filter dropdown refs for outside click
  const filterAnchorRef = useRef(null);
  const filterPanelRef = useRef(null);

  // ---------------- INIT (fetch recent once per cid) ----------------
  useEffect(() => {
    if (!cid) return;

    if (initDoneRef.current.cid !== cid) {
      initDoneRef.current = { cid, recentFetched: false, bootstrapTried: false };
      setLocalError("");
      setFiltersOpen(false);
      setQ("");
      setDateFrom("");
      setDateTo("");
      setStatusSet(new Set());
      log("cid changed -> reset ui + init state", { cid });
    }

    dispatch(markLastCitizenLocal({ citizenId: cid }));

    if (!initDoneRef.current.recentFetched) {
      initDoneRef.current.recentFetched = true;
      dispatch(fetchRecentLookups({ limit: 12 }));
    }
  }, [dispatch, cid]);

  // ---------------- BOOTSTRAP (once per cid) ----------------
  useEffect(() => {
    if (!cid) return;
    if (initDoneRef.current.cid !== cid) return;

    const activeId = activeCitizen?.id != null ? String(activeCitizen.id) : "";
    const hasMatchingActive = Boolean(activeId && activeId === cid);

    if (hasMatchingActive && !initDoneRef.current.bootstrapTried) {
      const phone10 = normalizePhone10(activeCitizen?.phone_number || activeCitizen?.phone || "");
      if (phone10 && phone10.length === 10) {
        initDoneRef.current.bootstrapTried = true;
        dispatch(refreshCitizenProfileThunk({ citizenId: cid }));
        return;
      }
    }

    if (initDoneRef.current.bootstrapTried) return;

    const serverArr = Array.isArray(recentServer) ? recentServer : [];
    if (!serverArr.length) return;

    const found = serverArr.find((x) => pickCitizenIdFromRecent(x) === cid);
    const phone10 = normalizePhone10(pickPhoneFromRecent(found));

    initDoneRef.current.bootstrapTried = true;

    if (phone10 && phone10.length === 10) {
      dispatch(lookupCitizenByPhoneThunk({ phone: phone10 }));
      return;
    }

    setLocalError("Vatandaş profili yüklenemedi. Lütfen sorgu ekranından tekrar arama yapınız.");
    logError("bootstrap failed (no phone source)", { cid, found });
  }, [dispatch, cid, activeCitizen, recentServer]);

  // ---------------- Derived citizen fields ----------------
  const citizenName = useMemo(() => {
    const full = titleCaseTr(`${safeText(activeCitizen?.name)} ${safeText(activeCitizen?.surname)}`.trim());
    return full && full !== "—" ? full : "Vatandaş";
  }, [activeCitizen]);

  const phoneDisplay = useMemo(() => formatPhoneDisplay(activeCitizen?.phone_number || activeCitizen?.phone || ""), [activeCitizen]);
  const email = useMemo(() => safeText(activeCitizen?.email || ""), [activeCitizen]);

  const canShowProfile = Boolean(activeCitizen && String(activeCitizen?.id || ""));

  // ---------------- Filtering + sorting ----------------
  const sortedApps = useMemo(() => {
    const arr = Array.isArray(apps) ? [...apps] : [];
    arr.sort((a, b) => {
      const da = new Date(pickStatusDate(a) || pickCreatedAt(a) || 0).getTime();
      const db = new Date(pickStatusDate(b) || pickCreatedAt(b) || 0).getTime();
      if (db !== da) return db - da;
      return Number(b?.id || 0) - Number(a?.id || 0);
    });
    return arr;
  }, [apps]);

  const filteredApps = useMemo(() => {
    const query = safeText(q).toLowerCase();
    const hasQuery = Boolean(query);
    const hasStatusFilter = statusSet.size > 0;
    const hasDateFilter = Boolean(dateFrom || dateTo);

    return sortedApps.filter((app) => {
      const st = String(app?.status || "").trim();

      if (hasStatusFilter && !statusSet.has(st)) return false;

      if (hasDateFilter) {
        const atRaw = pickStatusDate(app) || pickCreatedAt(app);
        const d = safeDateObj(atRaw);
        if (!isInDateRange(d, dateFrom, dateTo)) return false;
      }

      if (hasQuery) {
        const hay = [
          pickTracking(app),
          safeText(app?.subject),
          safeText(app?.description),
          pickUnitName(app),
          pickNeighborhood(app),
          pickAddress(app),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        if (!hay.includes(query)) return false;
      }

      return true;
    });
  }, [sortedApps, q, statusSet, dateFrom, dateTo]);

  // ---------------- Actions ----------------
  const handleGoLookup = useCallback(() => {
    navigate(`${BASE}/sorgu`, { replace: false });
  }, [navigate]);

  const handleNewApplication = useCallback(() => {
    if (!cid) return;
    navigate(`${BASE}/vatandas/${cid}/yeni-basvuru`, {
      replace: false,
      state: {
        prefillCitizen: {
          id: cid,
          name: activeCitizen?.name || "",
          surname: activeCitizen?.surname || "",
          phone_number: activeCitizen?.phone_number || activeCitizen?.phone || "",
          email: activeCitizen?.email || "",
        },
      },
    });
  }, [navigate, cid, activeCitizen]);

  const handleOpenApplication = useCallback(
    (applicationId) => {
      if (!applicationId) return;
      navigate(`${BASE}/basvuru/${applicationId}`, { replace: false });
    },
    [navigate]
  );

  const toggleStatus = useCallback((key) => {
    setStatusSet((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const clearFilters = useCallback(() => {
    setDateFrom("");
    setDateTo("");
    setStatusSet(new Set());
  }, []);

  const closeFilters = useCallback(() => setFiltersOpen(false), []);

  // ESC + outside click close for dropdown
  useEffect(() => {
    if (!filtersOpen) return;

    const onKey = (e) => {
      if (e.key === "Escape") closeFilters();
    };

    const onPointerDown = (e) => {
      const a = filterAnchorRef.current;
      const p = filterPanelRef.current;
      const t = e.target;
      if (!a || !p || !t) return;
      if (a.contains(t) || p.contains(t)) return;
      closeFilters();
    };

    window.addEventListener("keydown", onKey);
    window.addEventListener("pointerdown", onPointerDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", onPointerDown);
    };
  }, [filtersOpen, closeFilters]);

  const effectiveError = localError || (error ? String(error) : "");

  // ---------------- Render ----------------
  return (
    <div className="hm-page hm-citizen-profile-page">
      <div className="hm-shell hm-shell--full hm-appdetail-shell">
        {/* ===== Pagebar (DETAY SAYFASI İLE AYNI YAPI) ===== */}
        <div className="hm-ad-pagebar hm-ad-pagebar--static">
          <div className="hm-ad-pagebar-left">
            <div className="hm-ad-pagebar-title">Vatandaş Profili</div>
          </div>

          <div className="hm-ad-pagebar-actions">
            <button
              type="button"
              className="btn-md btn-md-primary hm-pagebar-primary"
              onClick={handleNewApplication}
              disabled={!cid || Boolean(loading)}
              title={!cid ? "Vatandaş bilgisi yok" : "Yeni başvuru oluştur"}
            >
              <PlusIcon width={18} height={18} />
              Yeni Başvuru Yap
            </button>
          </div>
        </div>

        {/* ===== Error banner ===== */}
        {effectiveError ? (
          <div className="hm-ad-alert hm-ad-alert--error" role="alert">
            <div className="hm-ad-alert-row">
              <XCircleIcon width={18} height={18} />
              <div className="hm-ad-alert-body">
                <div className="hm-ad-alert-title">Profil alınamadı</div>
                <div className="hm-ad-alert-text">{effectiveError}</div>
              </div>
            </div>

            <div className="hm-ad-alert-actions">
              <button type="button" className="btn-md btn-md-ghost" onClick={handleGoLookup}>
                Sorgu ekranına dön
              </button>
            </div>
          </div>
        ) : null}

        {/* ===== Main layout (35/65) ===== */}
        <div className="hm-ad-grid hm-ad-grid--3565 hm-cp-grid">
          {/* LEFT: citizen card (sticky değil) */}
          <aside className="hm-ad-left hm-cp-left">
            <StaticCard icon={UserIcon} title="İletişim Bilgileri">
              {canShowProfile ? (
                <KeyValueList
                  items={[
                    { key: "Ad Soyad", value: <span className="saha-wrap">{citizenName}</span> },
                    { key: "Telefon", value: <span className="saha-wrap">{phoneDisplay}</span> },
                    { key: "E-posta", value: <span className="saha-wrap">{email || "—"}</span> },
                  ]}
                />
              ) : (
                <div className="hm-muted">Vatandaş bilgileri yükleniyor…</div>
              )}
            </StaticCard>
          </aside>

          {/* RIGHT: applications list (KAPANMAZ) */}
          <main className="hm-ad-right hm-cp-right">
            <section className="md-card hm-apps-card">
              <div className="hm-apps-head">
                <div className="hm-apps-head-left">
                  <DocumentTextIcon width={18} height={18} className="hm-apps-head-ic" />
                  <div className="hm-apps-head-title">Başvuru Geçmişi</div>
                </div>

                <div className="hm-apps-head-right">
                  <span className="hm-apps-count">{loading ? "Yükleniyor…" : `${filteredApps.length} kayıt`}</span>
                </div>
              </div>

              <div className="hm-apps-toolbar">
                <div className="hm-top-search hm-top-search--inline" aria-label="Başvurularda ara">
                  <MagnifyingGlassIcon className="hm-top-search-ic" aria-hidden="true" />
                  <input
                    className="hm-top-search-input"
                    type="text"
                    placeholder="Başvurularda ara (konu, açıklama, birim, adres...)"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                  />
                  {q ? (
                    <button
                      type="button"
                      className="hm-top-search-clear"
                      onClick={() => setQ("")}
                      aria-label="Aramayı temizle"
                      title="Temizle"
                    >
                      <XMarkIcon width={18} height={18} />
                    </button>
                  ) : null}
                </div>

                <div className="hm-filter-anchor hm-filter-anchor--inline" ref={filterAnchorRef}>
                  <button
                    type="button"
                    className={`btn-md btn-md-ghost hm-filter-btn ${filtersOpen ? "is-open" : ""}`}
                    onClick={() => setFiltersOpen((p) => !p)}
                    aria-expanded={filtersOpen ? "true" : "false"}
                    aria-controls="hm_filters_dropdown"
                    title="Filtreler"
                  >
                    <FunnelIcon width={18} height={18} />
                    Filtreler
                  </button>

                  {filtersOpen ? (
                    <div className="md-card hm-filter-dropdown" id="hm_filters_dropdown" ref={filterPanelRef} role="dialog">
                      <div className="hm-filter-grid">
                        <div className="hm-filter-field">
                          <label className="hm-filter-label">
                            <CalendarDaysIcon width={16} height={16} /> Başlangıç
                          </label>
                          <input type="date" className="md-input" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                        </div>

                        <div className="hm-filter-field">
                          <label className="hm-filter-label">
                            <CalendarDaysIcon width={16} height={16} /> Bitiş
                          </label>
                          <input type="date" className="md-input" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                        </div>
                      </div>

                      <div className="hm-filter-status">
                        <div className="hm-filter-label">
                          <FunnelIcon width={16} height={16} /> Durum
                        </div>

                        <div className="hm-filter-chips">
                          {STATUS_OPTIONS.map((opt) => {
                            const active = statusSet.has(opt.key);
                            return (
                              <button
                                key={opt.key}
                                type="button"
                                className={`btn-md hm-filter-chip ${active ? "is-active" : ""}`}
                                onClick={() => toggleStatus(opt.key)}
                              >
                                {opt.label}
                              </button>
                            );
                          })}
                        </div>

                        <div className="hm-filter-actions">
                          <button type="button" className="btn-md btn-md-ghost" onClick={clearFilters}>
                            Temizle
                          </button>
                          <button type="button" className="btn-md btn-md-primary" onClick={closeFilters}>
                            Uygula
                          </button>
                        </div>
                      </div>

                      <div className="hm-footnote hm-footnote--compact">
                        Tarih filtresi: “durum tarihi” varsa onu, yoksa “oluşturulma” tarihini baz alır.
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="hm-apps-body">
                {loading ? <div className="hm-muted">Liste yükleniyor…</div> : null}

                {!loading && filteredApps.length === 0 ? <div className="hm-empty">Filtrelere uygun başvuru bulunamadı.</div> : null}

                {filteredApps.length > 0 ? (
                  <div className="hm-app-list hm-app-list--minimal">
                    {filteredApps.map((app) => {
                      const id = app?.id;

                      const subject = pickAppTitle(app);
                      const desc = pickAppDesc(app);
                      const tracking = pickTracking(app);

                      const unitName = pickUnitName(app);
                      const neighborhood = pickNeighborhood(app);
                      const address = pickAddress(app);

                      const stRaw = String(app?.status || "").trim();
                      const chip = statusChip(stRaw);
                      const ChipIcon = chip.icon;

                      const atRaw = pickStatusDate(app) || pickCreatedAt(app);
                      const hasLatLng = Boolean(app?.latitude && app?.longitude);

                      return (
                        <button
                          key={String(id || Math.random())}
                          type="button"
                          className="hm-app-row-min"
                          onClick={() => handleOpenApplication(id)}
                          aria-label={`Başvuru detayı: ${subject}`}
                        >
                          <div className="hm-app-row-min-top">
                            <div className="hm-app-row-min-title">{subject}</div>
                            <span className={chip.cls}>
                              {ChipIcon ? <ChipIcon width={16} height={16} /> : null}
                              {chip.label}
                            </span>
                          </div>

                          {desc ? <div className="hm-app-row-min-desc">{desc}</div> : null}

                          <div className="hm-app-row-min-meta">
                            {tracking ? (
                              <span className="hm-meta-pill">
                                <HashtagIcon width={16} height={16} />
                                {tracking}
                              </span>
                            ) : null}

                            {atRaw ? (
                              <span className="hm-meta-pill">
                                <CalendarDaysIcon width={16} height={16} />
                                <TrDateTime value={atRaw} />
                              </span>
                            ) : null}

                            {unitName ? (
                              <span className="hm-meta-pill">
                                <BuildingOffice2Icon width={16} height={16} />
                                {unitName}
                              </span>
                            ) : null}

                            {neighborhood ? (
                              <span className="hm-meta-pill">
                                <HomeModernIcon width={16} height={16} />
                                {neighborhood}
                              </span>
                            ) : null}

                            {address ? (
                              <span className="hm-meta-pill hm-meta-pill--soft" title={address}>
                                <MapPinIcon width={16} height={16} />
                                {address}
                              </span>
                            ) : null}

                            {hasLatLng ? (
                              <span className="hm-meta-pill">
                                <MapPinIcon width={16} height={16} />
                                Konum
                              </span>
                            ) : null}
                          </div>

                          <div className="hm-app-row-min-go" aria-hidden="true">
                            <ChevronRightIcon width={18} height={18} />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : null}

                <div className="hm-footnote hm-footnote--compact">
                  Arama: takip no, konu, açıklama, birim, mahalle ve adres alanlarında eşleşme arar.
                </div>
              </div>
            </section>
          </main>
        </div>

        <div className="hm-bottom-space" />
      </div>
    </div>
  );
}